import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';
import {
  FaEye, FaQrcode, FaUsers, FaBoxOpen, FaTrophy, FaClock, FaCalendarAlt,
  FaChartLine, FaUserCheck, FaSearch, FaFilter, FaDownload, FaSpinner, FaChartBar
} from 'react-icons/fa';

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const ShowroomAnalytics = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return {
      startDate: toLocalDateStr(start),
      endDate: toLocalDateStr(end),
      activityType: 'all',
      userId: '',
      page: 1,
      limit: 20
    };
  });
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    const sync = () => setIsDarkMode(localStorage.getItem('darkMode') === 'true');
    window.addEventListener('storage', sync);
    const interval = setInterval(sync, 500);
    return () => { window.removeEventListener('storage', sync); clearInterval(interval); };
  }, []);

  // Dark mode classes
  const bg = isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900';
  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const cardText = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const inputCls = isDarkMode
    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500'
    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500';
  const hoverRow = isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50';
  const borderCls = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '32px',
      fontSize: '14px',
      backgroundColor: isDarkMode ? '#374151' : '#fff',
      borderColor: isDarkMode ? '#4b5563' : '#d1d5db',
      color: isDarkMode ? '#f3f4f6' : '#111827',
      '&:hover': { borderColor: isDarkMode ? '#6b7280' : '#9ca3af' },
    }),
    singleValue: (base) => ({ ...base, color: isDarkMode ? '#f3f4f6' : '#111827' }),
    input: (base) => ({ ...base, color: isDarkMode ? '#f3f4f6' : '#111827' }),
    placeholder: (base) => ({ ...base, color: isDarkMode ? '#9ca3af' : '#6b7280' }),
    menu: (base) => ({ ...base, backgroundColor: isDarkMode ? '#374151' : '#fff', zIndex: 50 }),
    option: (base, state) => ({
      ...base,
      fontSize: '14px',
      backgroundColor: state.isFocused
        ? (isDarkMode ? '#4b5563' : '#eff6ff')
        : (isDarkMode ? '#374151' : '#fff'),
      color: isDarkMode ? '#f3f4f6' : '#111827',
      '&:active': { backgroundColor: isDarkMode ? '#6b7280' : '#dbeafe' },
    }),
  };

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 10000,
    withCredentials: true,
  });

  const makeAuthenticatedRequest = async (method, url, options = {}) => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      throw new Error('No authentication token found');
    }
    return api[method](url, options);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [user]);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    if (!user?.user) return;
    try {
      setLoading(true);
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await makeAuthenticatedRequest('get', '/company-head/showroom/dashboard', { params });
      setDashboardData(response.data.dashboard);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.message === 'No authentication token found') {
        Swal.fire({
          title: 'Authentication Error',
          text: 'Please log in again to access this feature.',
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch activities
  const fetchActivities = async () => {
    if (!user?.user) return;
    try {
      setActivitiesLoading(true);
      const params = { ...filters };
      
      const response = await makeAuthenticatedRequest('get', '/company-head/showroom/activities', { params });
      setActivities(response.data.activities);
    } catch (error) {
      console.error('Error fetching activities:', error);
      if (error.message === 'No authentication token found') {
        Swal.fire({
          title: 'Authentication Error',
          text: 'Please log in again to access this feature.',
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
      }
    } finally {
      setActivitiesLoading(false);
    }
  };

  // Fetch employees for filter dropdown
  const fetchEmployees = async () => {
    if (!user?.user?.company) return;
    try {
      const params = {};
      if (['Admin', 'SuperAdmin'].includes(user?.role) && filters.companyId) {
        params.companyId = filters.companyId;
      }
      const response = await api.get(('/employees/company2/'+ user.user.company), { params });
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    if (user && user.role === 'CompanyHead') {
      fetchDashboardData();
      fetchEmployees();
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchActivities();
  }, [user, filters]);

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Get last 7 days for default date range
  const getLast7Days = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    setFilters(prev => ({
      ...prev,
      startDate: toLocalDateStr(start),
      endDate: toLocalDateStr(end)
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      activityType: 'all',
      userId: '',
      page: 1,
      limit: 20
    });
  };

  // Export data
  const exportData = async () => {
    try {
      const result = await Swal.fire({
        title: 'Export Showroom Analytics Data?',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>Export Details:</strong></p>
            <ul class="text-sm">
              <li>📅 Date Range: ${filters.startDate || 'All dates'} to ${filters.endDate || 'Present'}</li>
              <li>📊 Activity Type: ${filters.activityType === 'all' ? 'All activities' : filters.activityType}</li>
              <li>👤 Employee: ${filters.userId ? employees.find(e => e._id === filters.userId)?.name || 'Selected employee' : 'All employees'}</li>
            </ul>
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#6b7280',
        confirmButtonText: '<i class="fas fa-download"></i> Export CSV',
        cancelButtonText: 'Cancel'
      });
      
      if (!result.isConfirmed) return;
      
      setActivitiesLoading(true);
      
      const exportParams = { 
        ...filters,
        limit: 10000,
        page: 1 
      };
      
      if (['Admin', 'SuperAdmin'].includes(user?.role) && filters.companyId) {
        exportParams.companyId = filters.companyId;
      }
      
      const response = await makeAuthenticatedRequest('get', '/company-head/showroom/activities', { params: exportParams });
      const exportActivities = response.data.activities || [];
      
      if (exportActivities.length === 0) {
        Swal.fire({
          title: 'No Data to Export',
          text: 'No activities found matching your current filters.',
          icon: 'info',
          confirmButtonColor: '#3b82f6'
        });
        return;
      }
      
      const csvContent = generateCSV(exportActivities);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const timestamp = new Date().toISOString().split('T')[0];
        const timeStr = new Date().toISOString().split('T')[1].substring(0, 8).replace(/:/g, '-');
        const filename = `Showroom-Analytics-${timestamp}-${timeStr}.csv`;
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        Swal.fire({
          title: 'Export Successful!',
          html: `<p>Exported ${exportActivities.length} records to ${filename}</p>`,
          icon: 'success',
          confirmButtonColor: '#10b981'
        });
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      Swal.fire({
        title: 'Export Failed',
        text: error.response?.data?.message || error.message || 'Unknown error',
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setActivitiesLoading(false);
    }
  };

  const generateCSV = (activities) => {
    const headers = ['Date & Time', 'Activity Type', 'Employee Name', 'Employee Email', 'Item Name', 'Item GUID'];
    let csvContent = headers.join(',') + '\n';
    activities.forEach(activity => {
      const row = [
        `"${formatDate(activity.timestamp)}"`,
        `"${activity.activityType?.toUpperCase() || 'UNKNOWN'}"`,
        `"${activity.userName?.replace(/"/g, '""') || 'N/A'}"`,
        `"${activity.userEmail || 'N/A'}"`,
        `"${activity.itemName?.replace(/"/g, '""') || 'N/A'}"`,
        `"${activity.itemGUID || 'N/A'}"`
      ];
      csvContent += row.join(',') + '\n';
    });
    return csvContent;
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${bg}`}>
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
        <span className="ml-2 text-lg">Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className={`p-6 w-full mx-auto min-h-screen ${bg}`}>
      {/* Header */}
      <div className="mb-6">
        <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Showroom Analytics
        </h1>
        <div className="flex items-center justify-between">
          <p className={cardText}>Track item views and QR scans by your team</p>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'
          }`}>
            💼 Company Head
          </span>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className={`mb-6 rounded-lg shadow border p-4 ${cardBg}`}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FaCalendarAlt className={cardText} />
            <label className={`text-sm font-medium ${cardText}`}>From:</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className={`border rounded px-3 py-1.5 text-sm ${inputCls}`}
            />
          </div>
          
          <div className="flex items-center gap-2">
            <label className={`text-sm font-medium ${cardText}`}>To:</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className={`border rounded px-3 py-1.5 text-sm ${inputCls}`}
            />
          </div>

          <button
            onClick={getLast7Days}
            className={`px-3 py-1.5 text-sm rounded font-medium ${
              isDarkMode ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-800/50' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            Last 7 Days
          </button>

          <button
            onClick={clearFilters}
            className={`px-3 py-1.5 text-sm rounded font-medium ${
              isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Clear Filters
          </button>

          <button
            onClick={fetchDashboardData}
            className="px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium text-sm"
          >
            Search
          </button>

          <button
            onClick={exportData}
            disabled={activitiesLoading}
            className={`px-4 py-1.5 rounded flex items-center font-medium text-sm ${
              activitiesLoading 
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {activitiesLoading ? (
              <><FaSpinner className="animate-spin mr-1" /> Exporting...</>
            ) : (
              <><FaDownload className="mr-1" /> Quick Export</>
            )}
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      {dashboardData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {[
              { icon: FaChartLine, color: 'blue', label: 'Total Activities', value: dashboardData.overview.totalActivities },
              { icon: FaEye, color: 'green', label: 'Item Views', value: dashboardData.overview.totalViews },
              { icon: FaQrcode, color: 'purple', label: 'QR Scans', value: dashboardData.overview.totalScans },
              { icon: FaUsers, color: 'orange', label: 'Active Users', value: dashboardData.overview.uniqueUsers },
              { icon: FaBoxOpen, color: 'teal', label: 'Items Accessed', value: dashboardData.overview.uniqueItemsAccessed },
            ].map(({ icon: Icon, color, label, value }, idx) => (
              <div key={idx} className={`rounded-lg shadow border p-4 ${cardBg}`}>
                <div className="flex items-center">
                  <Icon className={`text-2xl text-${color}-${isDarkMode ? '400' : '600'} mr-3`} />
                  <div>
                    <p className={`text-sm ${cardText}`}>{label}</p>
                    <p className="text-2xl font-bold">{value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Top Items and Employees */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Top Items */}
            <div className={`rounded-lg shadow border ${cardBg}`}>
              <div className={`p-4 border-b ${borderCls}`}>
                <h2 className="text-lg font-semibold flex items-center">
                  <FaTrophy className="text-yellow-500 mr-2" />
                  Most Accessed Items
                </h2>
              </div>
              <div className="p-4">
                {dashboardData.topItems && dashboardData.topItems.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.topItems.slice(0, 8).map((item, index) => (
                      <div key={item.itemId} className={`flex items-center justify-between p-2 rounded ${hoverRow}`}>
                        <div className="flex items-center">
                          <span className={`text-xs px-2 py-1 rounded-full mr-3 ${
                            isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'
                          }`}>
                            #{index + 1}
                          </span>
                          <div>
                            <p className="font-medium text-sm">{item.itemName}</p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>GUID: {item.itemGUID}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">{item.totalAccess}</p>
                          <p className={`text-xs ${cardText}`}>
                            {item.views} views, {item.scans} scans
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-8 ${cardText}`}>
                    <FaChartBar className="text-4xl mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No item access data available</p>
                    <p className="text-xs">Items will appear here once employees start viewing or scanning them</p>
                  </div>
                )}
              </div>
            </div>

            {/* Top Employees */}
            <div className={`rounded-lg shadow border ${cardBg}`}>
              <div className={`p-4 border-b ${borderCls}`}>
                <h2 className="text-lg font-semibold flex items-center">
                  <FaUserCheck className="text-green-500 mr-2" />
                  Most Active Employees
                </h2>
              </div>
              <div className="p-4">
                {dashboardData.employeeStats && dashboardData.employeeStats.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.employeeStats.slice(0, 8).map((employee, index) => (
                      <div key={employee.userId} className={`flex items-center justify-between p-2 rounded ${hoverRow}`}>
                        <div className="flex items-center">
                          <span className={`text-xs px-2 py-1 rounded-full mr-3 ${
                            isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'
                          }`}>
                            #{index + 1}
                          </span>
                          <div>
                            <p className="font-medium text-sm">{employee.userName}</p>
                            <p className={`text-xs ${cardText}`}>{employee.userEmail}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">{employee.totalActivities}</p>
                          <p className={`text-xs ${cardText}`}>
                            {employee.views} views, {employee.scans} scans
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-8 ${cardText}`}>
                    <FaUsers className="text-4xl mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No employee activity data available</p>
                    <p className="text-xs">Employee stats will appear here once they start using the showroom</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Recent Activities */}
      <div className={`rounded-lg shadow border ${cardBg}`}>
        <div className={`p-4 border-b ${borderCls}`}>
          <div className="flex flex-wrap justify-between items-center gap-4">
            <h2 className="text-lg font-semibold flex items-center">
              <FaClock className="text-blue-500 mr-2" />
              Recent Activities
            </h2>
            <div className="flex flex-wrap items-center gap-4">
              {/* Activity Type Filter */}
              <Select
                value={{ 
                  value: filters.activityType, 
                  label: filters.activityType === 'all' ? 'All Activities' : 
                         filters.activityType === 'view' ? 'Views Only' : 'Scans Only' 
                }}
                onChange={(selectedOption) => setFilters(prev => ({ 
                  ...prev, 
                  activityType: selectedOption.value, 
                  page: 1 
                }))}
                options={[
                  { value: 'all', label: 'All Activities' },
                  { value: 'view', label: 'Views Only' },
                  { value: 'scan', label: 'Scans Only' }
                ]}
                className="min-w-[140px]"
                classNamePrefix="react-select"
                isSearchable={false}
                styles={selectStyles}
              />

              {/* Employee Filter */}
              <Select
                value={employees.find(emp => emp._id === filters.userId) ? 
                  { value: employees.find(emp => emp._id === filters.userId)._id, 
                    label: `${employees.find(emp => emp._id === filters.userId).name} (${employees.find(emp => emp._id === filters.userId).id})` } : null}
                onChange={(selectedOption) => setFilters(prev => ({ 
                  ...prev, 
                  userId: selectedOption ? selectedOption.value : '', 
                  page: 1 
                }))}
                options={[
                  { value: '', label: 'All Employees' },
                  ...employees.map(emp => ({ 
                    value: emp._id, 
                    label: `${emp.name} (${emp.id})` 
                  }))
                ]}
                placeholder="Select Employee"
                isClearable
                className="min-w-[200px]"
                classNamePrefix="react-select"
                styles={selectStyles}
              />

              <button
                onClick={exportData}
                disabled={activitiesLoading}
                className={`px-3 py-1 rounded text-sm flex items-center font-medium ${
                  activitiesLoading 
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                    : isDarkMode 
                      ? 'bg-green-900/50 text-green-300 hover:bg-green-800/50' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {activitiesLoading ? (
                  <><FaSpinner className="animate-spin mr-1" /> Exporting...</>
                ) : (
                  <><FaDownload className="mr-1" /> Export CSV</>
                )}
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-4">
          {activitiesLoading ? (
            <div className="text-center py-4">
              <FaSpinner className="animate-spin text-2xl text-blue-600 mx-auto mb-2" />
              <p>Loading activities...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className={`border-b ${borderCls}`}>
                    <th className={`text-left py-2 text-sm font-medium ${cardText}`}>Time</th>
                    <th className={`text-left py-2 text-sm font-medium ${cardText}`}>Type</th>
                    <th className={`text-left py-2 text-sm font-medium ${cardText}`}>Employee</th>
                    <th className={`text-left py-2 text-sm font-medium ${cardText}`}>Item</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((activity) => (
                    <tr key={activity._id} className={`border-b ${borderCls} ${hoverRow}`}>
                      <td className="py-2 text-sm">
                        {formatDate(activity.timestamp)}
                      </td>
                      <td className="py-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                          activity.activityType === 'view' 
                            ? (isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800')
                            : (isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800')
                        }`}>
                          {activity.activityType === 'view' ? <FaEye className="mr-1" /> : <FaQrcode className="mr-1" />}
                          {activity.activityType}
                        </span>
                      </td>
                      <td className="py-2 text-sm">
                        <div>
                          <p className="font-medium">{activity.userName}</p>
                          <p className={`text-xs ${cardText}`}>{activity.userEmail}</p>
                        </div>
                      </td>
                      <td className="py-2 text-sm">
                        <div>
                          <p className="font-medium">{activity.itemName}</p>
                          <p className={`text-xs ${cardText}`}>GUID: {activity.itemGUID}</p>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {activities.length === 0 && (
                <div className={`text-center py-8 ${cardText}`}>
                  <FaSearch className="text-4xl mx-auto mb-2 opacity-30" />
                  <p>No activities found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowroomAnalytics;
